<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AltArrowLeftIcon, AltArrowRightIcon, CalendarDateIcon } from '@solar-icons/vue/linear'
import { useFormatters } from '@/composables/useFormatters'
import AppButton from '../AppButton/AppButton.vue'
import AppPopover from '../AppPopover/AppPopover.vue'
import AppSelect from '../AppSelect/AppSelect.vue'
import type { SelectOption } from '../AppSelect/select-types'
import type {
  DatePickerMode,
  DateRangeValue,
  DateTimeTriggerVariant,
  DateTimeValue,
} from './date-time-picker-types'

const props = withDefaults(
  defineProps<{
    /** The scheduled instant, or `null` when nothing is set. Single mode only; v-model. */
    modelValue?: DateTimeValue
    placeholder?: string
    ariaLabel?: string
    /** Earliest selectable instant; earlier days, hours and minutes cannot be picked. Defaults to now - pass `null` to allow the past. */
    minDate?: Date | null
    /** Shows the value but refuses to change it; the popover never opens. */
    readOnly?: boolean
    /** `single` takes one instant via `v-model`; `range` takes an inclusive day range via `v-model:range`. */
    mode?: DatePickerMode
    /** Longest selectable range, in days inclusive. Range mode only; days past the cap are disabled once a start is picked. */
    maxSpanDays?: number
    /** The trigger button's variant: `secondary` (default) or `ghost`. */
    triggerVariant?: DateTimeTriggerVariant
  }>(),
  {
    modelValue: null,
    placeholder: undefined,
    ariaLabel: undefined,
    minDate: undefined,
    readOnly: false,
    mode: 'single',
    maxSpanDays: undefined,
    triggerVariant: 'secondary',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: Date]
}>()

/** The picked day range, or `null` when nothing is set. Range mode only. */
const range = defineModel<DateRangeValue>('range', { default: null })

const { t } = useI18n()
const { formatDate, weekdayNarrow } = useFormatters()

const isRange = computed(() => props.mode === 'range')

const showTime = computed(() => !isRange.value)

const resolvedPlaceholder = computed(
  () =>
    props.placeholder ??
    (isRange.value
      ? t('primitives.dateTimePicker.setDates')
      : t('primitives.dateTimePicker.setDateTime')),
)

const open = ref(false)

const DEFAULT_HOUR_24 = 9
const DEFAULT_MINUTE = 0
const MINUTE_STEP = 5

const floor = ref<Date | null>(null)

function resolveFloor(): Date | null {
  if (props.minDate === null) return null
  const base = props.minDate ?? new Date()
  const rounded = new Date(base)
  rounded.setSeconds(0, 0)
  const remainder = rounded.getMinutes() % MINUTE_STEP
  if (remainder !== 0) rounded.setMinutes(rounded.getMinutes() + (MINUTE_STEP - remainder))
  return rounded
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function seedStaged(): Date {
  const base = props.modelValue ?? new Date()
  const seed = props.modelValue
    ? new Date(base)
    : new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate(),
        DEFAULT_HOUR_24,
        DEFAULT_MINUTE,
        0,
        0,
      )
  const min = floor.value
  return min && seed.getTime() < min.getTime() ? new Date(min) : seed
}

const staged = ref<Date>(seedStaged())
const stagedStart = ref<Date | null>(null)
const stagedEnd = ref<Date | null>(null)
const hoverDate = ref<Date | null>(null)

watch(open, (isOpen) => {
  if (!isOpen) {
    hoverDate.value = null
    return
  }
  floor.value = resolveFloor()
  staged.value = seedStaged()
  stagedStart.value = range.value ? startOfDay(range.value.start) : null
  stagedEnd.value = range.value ? startOfDay(range.value.end) : null
  hoverDate.value = null
})

const WEEKDAY_LABELS = computed(() => Array.from({ length: 7 }, (_, day) => weekdayNarrow(day)))

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function mondayOf(date: Date): Date {
  const stripped = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const day = stripped.getDay()
  const diffToMonday = day === 0 ? -6 : 1 - day
  return addDays(stripped, diffToMonday)
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function initialViewMonth(): Date {
  if (props.mode === 'range') return startOfMonth(range.value?.start ?? new Date())
  return startOfMonth(props.modelValue ?? new Date())
}

const viewMonth = ref<Date>(initialViewMonth())

watch(open, (isOpen) => {
  if (!isOpen) return
  viewMonth.value = isRange.value
    ? startOfMonth(stagedStart.value ?? floor.value ?? new Date())
    : startOfMonth(staged.value)
})

const today = new Date()

interface DayCell {
  date: Date
  inCurrentMonth: boolean
  isToday: boolean
  disabled: boolean
  blank: boolean
}

interface MonthView {
  key: string
  label: string
  days: DayCell[]
}

function isDayDisabled(date: Date): boolean {
  const min = floor.value
  if (min !== null && startOfDay(date).getTime() < startOfDay(min).getTime()) return true
  if (!isRange.value) return false
  const cap = props.maxSpanDays
  const start = stagedStart.value
  if (cap === undefined || !start || stagedEnd.value) return false
  const last = addDays(startOfDay(start), cap - 1)
  return startOfDay(date).getTime() > last.getTime()
}

function buildGrid(month: Date): DayCell[] {
  const gridStart = mondayOf(startOfMonth(month))
  const cells = Array.from({ length: 42 }, (_, i) => {
    const date = addDays(gridStart, i)
    const inCurrentMonth = date.getMonth() === month.getMonth()
    return {
      date,
      inCurrentMonth,
      isToday: isSameDay(date, today),
      disabled: isDayDisabled(date),
      blank: isRange.value && !inCurrentMonth,
    }
  })
  if (!isRange.value) return cells
  let end = cells.length
  while (end > 7 && cells.slice(end - 7, end).every((cell) => cell.blank)) end -= 7
  return cells.slice(0, end)
}

const months = computed<MonthView[]>(() => {
  const first = startOfMonth(viewMonth.value)
  const list = isRange.value
    ? [first, new Date(first.getFullYear(), first.getMonth() + 1, 1)]
    : [first]
  return list.map((month) => ({
    key: `${month.getFullYear()}-${month.getMonth()}`,
    label: formatDate(month, 'monthYear'),
    days: buildGrid(month),
  }))
})

const previewEnd = computed<Date | null>(() => {
  if (!isRange.value) return null
  if (stagedEnd.value) return stagedEnd.value
  const start = stagedStart.value
  const hover = hoverDate.value
  if (!start || !hover) return null
  return hover.getTime() >= startOfDay(start).getTime() ? hover : null
})

interface RangeState {
  isStart: boolean
  isEnd: boolean
  inBand: boolean
}

function rangeStateOf(date: Date): RangeState {
  const start = stagedStart.value
  if (!isRange.value || !start) return { isStart: false, isEnd: false, inBand: false }
  const day = startOfDay(date).getTime()
  const from = startOfDay(start).getTime()
  const end = previewEnd.value
  const to = end ? startOfDay(end).getTime() : from
  return { isStart: day === from, isEnd: day === to, inBand: day >= from && day <= to }
}

function isSelected(date: Date): boolean {
  if (isRange.value) {
    const state = rangeStateOf(date)
    return state.isStart || state.isEnd
  }
  return isSameDay(date, staged.value)
}

function bandClasses(cell: DayCell): string[] {
  const { isStart, isEnd, inBand } = rangeStateOf(cell.date)
  if (!inBand || cell.disabled || cell.blank) return []
  return ['bg-accent-blue/10', isStart ? 'rounded-l-full' : '', isEnd ? 'rounded-r-full' : '']
}

function onDayHover(cell: DayCell): void {
  if (isRange.value && !cell.blank && !cell.disabled) hoverDate.value = cell.date
}

const atFirstMonth = computed(() => {
  const min = floor.value
  if (!min) return false
  return (
    viewMonth.value.getFullYear() === min.getFullYear() &&
    viewMonth.value.getMonth() === min.getMonth()
  )
})

function shiftMonth(delta: number): void {
  if (delta < 0 && atFirstMonth.value) return
  viewMonth.value = new Date(viewMonth.value.getFullYear(), viewMonth.value.getMonth() + delta, 1)
}

const activeTime = computed(() => ({
  h24: staged.value.getHours(),
  minute: staged.value.getMinutes(),
}))

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

const floorTimeToday = computed<{ hour: number; minute: number } | null>(() => {
  const min = floor.value
  if (!min || !isSameDay(min, staged.value)) return null
  return { hour: min.getHours(), minute: min.getMinutes() }
})

const HOUR_OPTIONS = computed<SelectOption[]>(() => {
  const earliest = floorTimeToday.value?.hour ?? 0
  return Array.from({ length: 24 - earliest }, (_, i) => ({
    value: String(earliest + i),
    label: pad2(earliest + i),
  }))
})

const MINUTE_OPTIONS = computed<SelectOption[]>(() => {
  const barred = floorTimeToday.value
  const earliest = barred && barred.hour === activeTime.value.h24 ? barred.minute : 0
  const steps = new Set<number>()
  for (let m = 0; m < 60; m += MINUTE_STEP) if (m >= earliest) steps.add(m)
  steps.add(activeTime.value.minute)
  return [...steps].sort((a, b) => a - b).map((m) => ({ value: String(m), label: pad2(m) }))
})

function baseDay(): { y: number; m: number; d: number } {
  const d = staged.value
  return { y: d.getFullYear(), m: d.getMonth(), d: d.getDate() }
}

function setStaged(y: number, m: number, d: number, h24: number, min: number): void {
  const next = new Date(y, m, d, h24, min, 0, 0)
  const earliest = floor.value
  staged.value = earliest && next.getTime() < earliest.getTime() ? new Date(earliest) : next
}

function selectDay(date: Date): void {
  if (isDayDisabled(date)) return
  if (isRange.value) {
    const day = startOfDay(date)
    const start = stagedStart.value
    if (!start || stagedEnd.value || day.getTime() < startOfDay(start).getTime()) {
      stagedStart.value = day
      stagedEnd.value = null
      return
    }
    stagedEnd.value = day
    return
  }
  setStaged(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    activeTime.value.h24,
    activeTime.value.minute,
  )
}

function onHourChange(value: string): void {
  const { y, m, d } = baseDay()
  setStaged(y, m, d, Number(value), activeTime.value.minute)
}

function onMinuteChange(value: string): void {
  const { y, m, d } = baseDay()
  setStaged(y, m, d, activeTime.value.h24, Number(value))
}

const confirmDisabled = computed(() => isRange.value && (!stagedStart.value || !stagedEnd.value))

function confirm(): void {
  if (isRange.value) {
    const start = stagedStart.value
    const end = stagedEnd.value
    if (!start || !end) return
    range.value = { start, end }
    open.value = false
    return
  }
  emit('update:modelValue', staged.value)
  open.value = false
}

const triggerWrapper = ref<HTMLElement | null>(null)

/** Moves keyboard focus to the trigger, with its focus ring showing. */
function focus(): void {
  triggerWrapper.value?.querySelector('button')?.focus({ focusVisible: true })
}

defineExpose({ focus })

const formatted = computed(() => {
  if (isRange.value) {
    const picked = range.value
    if (!picked) return resolvedPlaceholder.value
    if (isSameDay(picked.start, picked.end)) return formatDate(picked.start, 'weekdayMonthDay')
    return `${formatDate(picked.start, 'monthDay')} – ${formatDate(picked.end, 'monthDay')}`
  }
  const d = props.modelValue
  if (!d) return resolvedPlaceholder.value
  return `${formatDate(d, 'weekday')} ${formatDate(d, 'month')} ${d.getDate()} · ${formatDate(d, 'time')}`
})
</script>

<template>
  <AppPopover v-model:open="open" placement="bottom-start" aria-haspopup="dialog">
    <template #trigger="{ aria }">
      <span ref="triggerWrapper" class="contents">
        <AppButton
          :variant="triggerVariant"
          :label="formatted"
          :start-icon="CalendarDateIcon"
          :aria-label="ariaLabel"
          :is-inactive="readOnly"
          v-bind="aria"
        />
      </span>
    </template>

    <div class="flex w-max flex-col gap-12 p-8">
      <div class="flex items-start gap-12">
        <div
          v-for="(view, index) in months"
          :key="view.key"
          role="group"
          :aria-label="view.label"
          class="flex flex-col gap-6"
        >
          <div class="flex items-center justify-between gap-4">
            <AppButton
              size="sm"
              v-if="index === 0"
              variant="ghost"
              icon-only
              :start-icon="AltArrowLeftIcon"
              :is-inactive="atFirstMonth"
              :aria-label="t('primitives.dateTimePicker.previousMonth')"
              @click="shiftMonth(-1)"
            />
            <span v-else class="invisible" aria-hidden="true">
              <AppButton size="sm" variant="ghost" icon-only :start-icon="AltArrowLeftIcon" />
            </span>
            <span class="flex-1 text-center text-body-sm-medium text-text-primary">
              {{ view.label }}
            </span>
            <AppButton
              size="sm"
              v-if="index === months.length - 1"
              variant="ghost"
              icon-only
              :start-icon="AltArrowRightIcon"
              :aria-label="t('primitives.dateTimePicker.nextMonth')"
              @click="shiftMonth(1)"
            />
            <span v-else class="invisible" aria-hidden="true">
              <AppButton size="sm" variant="ghost" icon-only :start-icon="AltArrowRightIcon" />
            </span>
          </div>

          <div class="grid grid-cols-7">
            <span
              v-for="(label, day) in WEEKDAY_LABELS"
              :key="day"
              class="flex items-center justify-center py-2 text-caption text-text-tertiary"
            >
              {{ label }}
            </span>
          </div>

          <div :class="['grid grid-cols-7', isRange ? '' : 'gap-1']" @mouseleave="hoverDate = null">
            <div
              v-for="cell in view.days"
              :key="cell.date.toISOString()"
              :class="['flex h-20 items-center justify-center', ...bandClasses(cell)]"
              @mouseenter="onDayHover(cell)"
            >
              <button
                v-if="!cell.blank"
                type="button"
                :disabled="cell.disabled"
                :aria-label="formatDate(cell.date, 'fullDay')"
                :aria-pressed="isSelected(cell.date)"
                :class="[
                  'flex size-20 items-center justify-center rounded-full text-body-sm',
                  'outline-2 outline-offset-2 outline-transparent focus-visible:outline-accent-blue',
                  'transition-colors duration-fast ease-soft motion-reduce:transition-none',
                  cell.disabled
                    ? 'cursor-not-allowed text-text-tertiary opacity-40'
                    : 'cursor-pointer hover:bg-bg-tertiary',
                  cell.disabled
                    ? ''
                    : isSelected(cell.date)
                      ? 'bg-accent-blue text-text-on-accent hover:bg-accent-blue'
                      : cell.isToday
                        ? 'text-accent-blue'
                        : cell.inCurrentMonth
                          ? 'text-text-primary'
                          : 'text-text-tertiary',
                ]"
                @click="selectDay(cell.date)"
              >
                {{ cell.date.getDate() }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showTime" class="flex flex-col gap-4">
        <span class="text-caption text-text-tertiary">{{
          t('primitives.dateTimePicker.time')
        }}</span>
        <div class="flex items-center gap-4">
          <AppSelect
            :aria-label="t('primitives.dateTimePicker.hour')"
            :options="HOUR_OPTIONS"
            :model-value="String(activeTime.h24)"
            @update:model-value="onHourChange"
          />
          <span class="text-body-sm text-text-tertiary">:</span>
          <AppSelect
            :aria-label="t('primitives.dateTimePicker.minute')"
            :options="MINUTE_OPTIONS"
            :model-value="String(activeTime.minute)"
            @update:model-value="onMinuteChange"
          />
        </div>
      </div>

      <div class="flex justify-end">
        <AppButton
          variant="primary"
          size="md"
          :label="t('primitives.dateTimePicker.set')"
          :is-inactive="confirmDisabled"
          @click="confirm"
        />
      </div>
    </div>
  </AppPopover>
</template>
